import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { eventMenuApiClient } from '@src/api/company/menu/eventMenuApiClient';
import { COMPANY_URL } from '@src/routes/company';
import React, { createContext, useCallback, useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';

type EventMenuItem = {
  title: string;
  link: string;
  isCurrent: boolean;
};

type EventMenuContextType = {
  eventMenus: EventMenuItem[];
  loading: boolean;
  loadEventMenus: () => Promise<void>;
};

const EventMenuContext = createContext<EventMenuContextType | undefined>(undefined);

export const EventMenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [eventMenus, setEventMenus] = useState<EventMenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const loadEventMenus = useCallback(async () => {
    setLoading(true);
    const res = await eventMenuApiClient.index();
    const query = new URLSearchParams(location.search);
    const eventProgress = query.get('event_progress');
    const pathName = location.pathname + location.search;

    handleApiResponse(res, (data) => {
      const formattedEventMenu = [
        {
          link: COMPANY_URL.EVENTS,
          title: 'すべてのイベント',
          isCurrent:
            pathName === COMPANY_URL.EVENTS ||
            pathName.includes(`${COMPANY_URL.EVENTS}/`) ||
            (query.size > 0 && !eventProgress),
        },
        ...data.map((item) => ({
          title: item.title,
          link: item.link,
          isCurrent: item.link.includes(`event_progress=${eventProgress}`),
        })),
      ];
      setEventMenus(formattedEventMenu);
    });
    setLoading(false);
  }, [location.pathname, location.search]);

  return (
    <EventMenuContext.Provider
      value={{
        eventMenus,
        loading,
        loadEventMenus,
      }}
    >
      {children}
    </EventMenuContext.Provider>
  );
};

export const useEventMenu = () => {
  const context = useContext(EventMenuContext);
  if (context === undefined) {
    throw new Error('useEventMenu must be used within an EventMenuProvider');
  }
  return context;
};
